<template>
  <div class="reports-page-container">
    <div class="page-header">
      <div class="header-content">
        <h1>前沿产业研究报告</h1>
        <p>深度解析行业趋势，赋能科技创新决策</p>

        <el-button @click="$router.push('/')" round size="small" class="back-btn">
          <el-icon class="el-icon--left"><Back /></el-icon> 返回首页
        </el-button>

      </div>
    </div>

    <div class="main-content">
      <div class="filter-tabs">
        <el-tabs v-model="activeCategory" @tab-click="handleTabClick" class="category-tabs">
          <el-tab-pane label="全部" name="all"></el-tab-pane>
          <el-tab-pane label="人工智能" name="ai"></el-tab-pane>
          <el-tab-pane label="新材料" name="material"></el-tab-pane>
          <el-tab-pane label="高端制造" name="manufacturing"></el-tab-pane>
          <el-tab-pane label="合成生物" name="bio"></el-tab-pane>
          <el-tab-pane label="生物医药" name="medical"></el-tab-pane>
        </el-tabs>
      </div>

      <div class="reports-list" v-loading="loading">
        <template v-if="reports.length > 0">
          <div v-for="item in reports" :key="item.id" class="report-item-card" data-aos="fade-up">
            <div class="report-icon">
              <el-icon><Document /></el-icon>
            </div>
            <div class="report-info">
              <div class="report-meta">
                <el-tag size="small" :type="getTagType(item.field)">{{ item.field_display }}</el-tag>
                <span class="date">{{ item.publish_date }}</span>
              </div>
              <h3 class="report-title" :title="item.title">{{ item.title }}</h3>
            </div>
            <div class="report-action">
              <el-button type="primary" plain round @click="openReport(item)">
                阅读报告 <el-icon class="el-icon--right"><Right /></el-icon>
              </el-button>
            </div>
          </div>
        </template>

        <el-empty v-else description="暂无该分类的研究报告" :image-size="120"></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Document, Right, Back } from '@element-plus/icons-vue'

export default {
  name: 'ReportsView',
  components: { Document, Right, Back },
  data() {
    return {
      activeCategory: 'all',
      reports: [],
      loading: false,
    };
  },
  mounted() {
    // 1. 获取路由参数中的 category (例如从首页点进来是 ?category=ai)
    const queryCategory = this.$route.query.category;
    if (queryCategory) {
      this.activeCategory = queryCategory;
    }
    // 2. 加载数据
    this.fetchReports();
  },
  methods: {
    // 切换 Tab 时触发
    handleTabClick(tab) {
      // 更新路由参数，但不刷新页面，方便用户分享链接
      this.$router.replace({ query: { category: tab.props.name === 'all' ? undefined : tab.props.name } });
      this.fetchReports();
    },

    // 获取数据
    async fetchReports() {
      this.loading = true;
      try {
        let url = '/members/api/public/reports/';
        // 如果不是"全部"，则拼接参数
        if (this.activeCategory !== 'all') {
          url += `?category=${this.activeCategory}`;
        }

        const response = await axios.get(url);
        if (response.data.results) {
            // 如果后端开启了分页，数据在 results 字段里
            this.reports = response.data.results;
        } else {
            // 如果后端没开启分页，数据直接就是 response.data
            this.reports = response.data;
        }
      } catch (error) {
        console.error("获取研报失败", error);
        this.$message.error("获取报告列表失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },

    // 打开报告链接逻辑
    openReport(item) {
      if (item.link) {
        // 如果有外部链接 (link 字段)
        window.open(item.link, '_blank');
      } else if (item.file) {
        // 如果是上传的文件 (file 字段)
        window.open(item.file, '_blank');
      } else {
        this.$message.warning("该报告暂无预览链接");
      }
    },

    // 辅助样式：不同分类显示不同颜色的标签
    getTagType(fieldCode) {
      const map = {
        'AI': '', // 默认蓝色
        'MATERIAL': 'success',
        'MANUFACTURING': 'warning',
        'SYNTHETIC_BIO': 'danger',
        'BIOMEDICINE': 'info'
      };
      return map[fieldCode] || '';
    }
  }
};
</script>

<style scoped>
.reports-page-container {
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 头部样式 */
.page-header {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #fff;
  padding: 60px 20px;
  text-align: center;
  position: relative;
}
.header-content h1 { font-size: 2.5rem; margin-bottom: 15px; letter-spacing: 2px; }
.header-content p { font-size: 1.1rem; opacity: 0.9; margin-bottom: 20px; }
.back-btn { background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.4); color: #fff; }
.back-btn:hover { background: #fff; color: #1e3c72; border-color: #fff; }

.main-content {
  max-width: 1000px;
  margin: -30px auto 40px; /* 向上重叠一点头部 */
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

/* 筛选 Tab 样式 */
.filter-tabs {
  background: #fff;
  padding: 10px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  margin-bottom: 20px;
}
:deep(.el-tabs__nav-wrap::after) { height: 1px; background-color: #eee; }
:deep(.el-tabs__item) { font-size: 16px; font-weight: 500; }

/* 列表样式 */
.reports-list { display: flex; flex-direction: column; gap: 15px; min-height: 300px; }

.report-item-card {
  background: #fff;
  padding: 25px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}
.report-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  border-color: #ecf5ff;
}

.report-icon {
  width: 50px; height: 50px;
  background: #ecf5ff; color: #409EFF;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px;
  margin-right: 20px;
  flex-shrink: 0;
}

.report-info { flex-grow: 1; overflow: hidden; }
.report-meta { margin-bottom: 8px; display: flex; align-items: center; gap: 10px; }
.date { font-size: 0.85rem; color: #999; }
.report-title {
  margin: 0; font-size: 1.1rem; color: #333;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.report-action { margin-left: 20px; flex-shrink: 0; }

@media (max-width: 768px) {
  .report-item-card { flex-direction: column; align-items: flex-start; }
  .report-icon { margin-bottom: 15px; }
  .report-action { margin-left: 0; margin-top: 15px; width: 100%; }
  .report-action button { width: 100%; }
}
</style>